import { useState, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  useTheme,
  Box,
  Typography,
  Stack,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
  alpha,
  Fade,
  Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";

const TIPO_CONFIG = {
  0: { route: "geo", label: "Geo", color: "primary" },
  1: { route: "top", label: "Top", color: "secondary" },
  2: { route: "top", label: "Pop", color: "warning" },
  3: { route: "onus", label: "Ftth", color: "info" },
  4: { route: "ping", label: "Ping", color: "success" },
};

const TitleBar = ({ title, id }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { maps } = useOutletContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const open = Boolean(anchorEl);

  const filteredMaps = useMemo(() => {
    if (!maps) return [];
    if (!searchQuery.trim()) return maps;
    const query = searchQuery.toLowerCase().trim();
    return maps.filter((map) => map.titulo?.toLowerCase().includes(query));
  }, [maps, searchQuery]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setSearchQuery("");
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery("");
  };

  const handleMapClick = (map) => {
    handleClose();
    if (map.id === id) return;
    const tipoConfig = TIPO_CONFIG[map.tipo];
    if (tipoConfig) {
      navigate(`/easyMaps/mapa/${tipoConfig.route}/${map.id}`);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        maxWidth: "400px",
        position: "absolute",
        left: 0,
        right: 0,
        top: { xs: -47, sm: -50 },
        marginLeft: "auto",
        marginRight: "auto",
        zIndex: 10,
        pointerEvents: "none",
        "& > *": { pointerEvents: "auto" },
      }}
    >
      {/* Trigger */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={handleClick}
        sx={{
          pl: 2,
          pr: 1,
          py: 0.75,
          borderRadius: "20px",
          backgroundColor: theme.palette.background.paperSelectedMaps,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0px 4px 8px #00000045",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          userSelect: "none",
          "&:hover": {
            backgroundColor: theme.palette.background.hover,
          },
        }}
      >
        <Typography
          sx={{
            maxWidth: { xs: "130px", sm: "170px", md: "240px" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "0.95rem",
            fontWeight: 600,
            letterSpacing: "0.3px",
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: "1.2rem",
            color: theme.palette.text.secondary,
            transition: "transform 0.2s ease",
            ...(open && { transform: "rotate(180deg)" }),
          }}
        />
      </Stack>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          elevation: 2,
          sx: {
            width: 380,
            maxHeight: 500,
            borderRadius: "12px",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            mt: 1.5,
          },
        }}
      >
        {/* Search */}
        <Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
          <TextField
            fullWidth
            placeholder="Encontre um mapa..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            variant="outlined"
            size="small"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "1.2rem",
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider sx={{ opacity: 0.5 }} />

        {/* List */}
        <List
          sx={{
            p: 1.5,
            pt: 1,
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          {filteredMaps.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 5,
                textAlign: "center",
              }}
            >
              <SearchIcon
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.4),
                  fontSize: "2rem",
                  mb: 1,
                }}
              />
              <Typography
                variant="small"
                sx={{ color: theme.palette.text.secondary }}
              >
                {searchQuery.trim()
                  ? "Nenhum mapa encontrado"
                  : "Nenhum mapa disponível"}
              </Typography>
            </Box>
          ) : (
            filteredMaps.map((map, index) => {
              const tipoConfig = TIPO_CONFIG[map.tipo];
              const isCurrent = map.id === id;

              return (
                <Box key={map.id}>
                  <ListItemButton
                    onClick={() => handleMapClick(map)}
                    selected={isCurrent}
                    sx={{
                      borderRadius: "6px",
                      py: 1,
                      px: 1.5,
                      mb: 0.5,
                      transition: "all 0.2s ease",
                      backgroundColor: isCurrent
                        ? theme.palette.background.hover
                        : "inherit",
                      "&:hover": {
                        backgroundColor: theme.palette.background.hover,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                        gap: 1.5,
                      }}
                    >
                      {isCurrent ? (
                        <CheckIcon
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: "1.2rem",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <MapOutlinedIcon
                          sx={{
                            color: theme.palette.text.disabled,
                            fontSize: "1.2rem",
                            flexShrink: 0,
                          }}
                        />
                      )}

                      <ListItemText
                        primary={map.titulo}
                        sx={{
                          m: 0,
                          "& .MuiListItemText-primary": {
                            fontSize: "0.9rem",
                            fontWeight: isCurrent ? 600 : 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: isCurrent
                              ? theme.palette.primary.main
                              : theme.palette.text.primary,
                          },
                        }}
                      />
                    </Box>

                    {tipoConfig && (
                      <Chip
                        label={tipoConfig.label}
                        color={tipoConfig.color}
                        size="small"
                        sx={{
                          ml: 1.5,
                          flexShrink: 0,
                          height: "24px",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </ListItemButton>
                  {index < filteredMaps.length - 1 && (
                    <Divider sx={{ mx: 1.5, my: 0.5, opacity: 0.5 }} />
                  )}
                </Box>
              );
            })
          )}
        </List>
      </Popover>
    </Box>
  );
};

export default TitleBar;
